import React from 'react'
import toast from 'react-hot-toast'
import { useApp } from '../../context/AppContext'
import { Icon } from '../../shared'
import { Btn } from '../../shared/components'
import { listProgramTrainers, assignTrainer, unassignTrainer } from '../../api/programs.api'

// ManageTrainersModal — assign/unassign trainers for a program.
// Satu-satunya jalur di frontend untuk ini; dipicu dari ApplicationsPanel
// (item "Kelola Trainer") dengan program aktif (`currentProgram`) dari
// AdminDashboardHome, menggantikan tombol yang dulu ada di tiap kartu
// program-grid (dihapus bersama redesain dashboard).
export function ManageTrainersModal({ program, onClose }) {
  const { lang, refreshPrograms } = useApp()
  const [trainers, setTrainers]   = React.useState([])
  const [loading, setLoading]     = React.useState(true)
  const [busy, setBusy]           = React.useState(false)

  // assign form state
  const [email, setEmail]         = React.useState("")
  const [fullName, setFullName]   = React.useState("")
  const [found, setFound]         = React.useState(null)  // null | false | user obj
  const [looking, setLooking]     = React.useState(false)
  const [assignResult, setAssignResult] = React.useState(null)

  const reload = React.useCallback(() => {
    setLoading(true)
    listProgramTrainers(program.id)
      .then(setTrainers)
      .catch((e) => toast.error(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false))
  }, [program.id])

  React.useEffect(() => { reload() }, [reload])

  const handleLookup = async (e) => {
    e.preventDefault()
    if (!email) return
    setLooking(true)
    setFound(null)
    setFullName("")
    try {
      const { lookupUserByEmail } = await import("../../api/users.api")
      const user = await lookupUserByEmail(email)
      setFound(user)
      setFullName(user.full_name)
    } catch (ex) {
      if (ex?.response?.status === 404) setFound(false)
      else toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setLooking(false)
    }
  }

  const handleAssign = async (e) => {
    e.preventDefault()
    setBusy(true)
    try {
      const payload = found ? { email } : { email, fullName }
      const res = await assignTrainer(program.id, payload)
      setAssignResult(res)
      toast.success(lang === "id" ? "Trainer di-assign & dinotifikasi" : "Trainer assigned & notified")
      setEmail(""); setFullName(""); setFound(null)
      reload()
      refreshPrograms()
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setBusy(false)
    }
  }

  const handleUnassign = async (userId) => {
    setBusy(true)
    try {
      await unassignTrainer(program.id, userId)
      reload()
      refreshPrograms()
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally {
      setBusy(false)
    }
  }

  const searched = found !== null

  return (
    <div
      className="modal-overlay"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "grid", placeItems: "center", zIndex: 50 }}
      onClick={onClose}
    >
      <div
        className="tile"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 500, maxHeight: "90vh", overflow: "auto", background: "var(--surface)" }}
      >
        <div className="tile-header">
          <h3>{lang === "id" ? "Kelola Trainer" : "Manage Trainers"} — {program.name}</h3>
          <Btn variant="ghost" size="sm" icon={<Icon name="close" size={14}/>} onClick={onClose}/>
        </div>

        <div className="col gap-4" style={{ padding: 16 }}>
          {/* Assigned list */}
          <div className="col gap-1">
            <span className="small" style={{ fontWeight: 600 }}>
              {lang === "id" ? "Trainer ter-assign" : "Assigned trainers"}
            </span>
            {loading ? (
              <div className="tiny muted">{lang === "id" ? "Memuat..." : "Loading..."}</div>
            ) : trainers.length === 0 ? (
              <div className="tiny muted">{lang === "id" ? "Belum ada trainer." : "No trainers yet."}</div>
            ) : (
              trainers.map((tr) => (
                <div key={tr.id} className="row between" style={{ padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 6 }}>
                  <div>
                    <div className="small" style={{ fontWeight: 500 }}>{tr.user?.full_name}</div>
                    <div className="tiny muted">{tr.user?.email}</div>
                  </div>
                  <Btn variant="ghost" size="sm" icon={<Icon name="trash" size={12}/>} disabled={busy} onClick={() => handleUnassign(tr.user_id)} style={{ color: "var(--danger)" }}/>
                </div>
              ))
            )}
          </div>

          {/* Assign new — email search */}
          <div className="col gap-2">
            <span className="small" style={{ fontWeight: 600 }}>
              {lang === "id" ? "Assign trainer baru" : "Assign a trainer"}
            </span>

            {assignResult?.password && (
              <div style={{ padding: 10, background: "var(--amber-10,#FFF8E1)", border: "1px solid var(--amber-50,#FBC02D)", borderRadius: 6 }}>
                <div className="small" style={{ fontWeight: 500, marginBottom: 2 }}>
                  {lang === "id" ? "Password sementara (catat!):" : "Temporary password (save now!):"}
                </div>
                <div className="mono" style={{ fontSize: 13, userSelect: "all" }}>{assignResult.password}</div>
              </div>
            )}

            <form onSubmit={handleLookup} className="row gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setFound(null); setFullName(""); setAssignResult(null) }}
                required
                placeholder="trainer@example.com"
                style={{ flex: 1, height: 32, padding: "0 8px", border: "1px solid var(--border-strong)", borderRadius: 4 }}
              />
              <Btn type="submit" variant="secondary" size="sm" disabled={looking || !email}>
                {looking ? "..." : (lang === "id" ? "Cari" : "Search")}
              </Btn>
            </form>

            {searched && found && (
              <div style={{ padding: 8, background: "var(--surface-2,#F8FAFC)", border: "1px solid var(--border-subtle)", borderRadius: 6, fontSize: 13 }}>
                <span style={{ color: "var(--text-3)", marginRight: 6 }}>{lang === "id" ? "Ditemukan:" : "Found:"}</span>
                <strong>{found.full_name}</strong>
                <span className="tiny muted" style={{ marginLeft: 6 }}>({found.role})</span>
                {found.role !== "TRAINER" && (
                  <div className="tiny muted mt-1">{lang === "id" ? "Role akan diubah ke TRAINER." : "Role will be changed to TRAINER."}</div>
                )}
              </div>
            )}

            {searched && found === false && (
              <div className="col gap-2">
                <div style={{ padding: 8, background: "var(--amber-10,#FFF8E1)", border: "1px solid var(--amber-30,#FFE082)", borderRadius: 6, fontSize: 12 }}>
                  {lang === "id" ? "Email belum ada — akun baru akan dibuat." : "Email not found — a new account will be created."}
                </div>
                <input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={lang === "id" ? "Nama lengkap trainer" : "Trainer full name"}
                  style={{ height: 32, padding: "0 8px", border: "1px solid var(--border-strong)", borderRadius: 4 }}
                />
              </div>
            )}

            {searched && (
              <form onSubmit={handleAssign}>
                <Btn type="submit" variant="primary" size="sm" disabled={busy || (found === false && !fullName)} block>
                  {busy ? (lang === "id" ? "Memproses..." : "Processing...")
                    : found ? (lang === "id" ? `Assign ${found.full_name}` : `Assign ${found.full_name}`)
                    : (lang === "id" ? "Buat Akun & Assign" : "Create Account & Assign")}
                </Btn>
              </form>
            )}

            <span className="tiny muted">
              {lang === "id" ? "Trainer akan menerima email notifikasi." : "Trainer will receive an email notification."}
            </span>
          </div>
        </div>

        <div className="tile-foot">
          <Btn variant="ghost" size="sm" onClick={onClose}>
            {lang === "id" ? "Tutup" : "Close"}
          </Btn>
        </div>
      </div>
    </div>
  )
}
